import { useContext } from 'react'
import { toJS } from 'mobx'
import { isEqual } from 'lodash'
import { useQueryClient } from '@tanstack/react-query'
import { StoresContext } from '@App/contexts'
import useSearchQuery from '@Components/Search/SearchPanel/useSearchQuery'
import { PaginationBody } from '@Entities/pagination'
import { Works } from '@Entities/work/works'
import { WorkSearch } from '@App/stores/search/models/work-search'
import { ClaimSearchFilter } from '@App/forms/filters/claim-search-filter'
import { DEFAULT_CLAIM_PAGINATION } from '@App/stores/search/claiming-tool-search-store'

export const CLAIM_SEARCH_QUERY_KEY = 'claim-search'

/**
 * Owns the TanStack Query wiring for the Claiming Tool's work search. The search criteria, filter
 * and page all live in the store, and so do the results — which outlive this page when the user
 * clicks through to a work and comes back, so they're only reused when they were fetched under the
 * exact key being asked for now.
 */
export const useClaimSearch = () => {
    const { claimingToolSearchStore, rightsholderStore } = useContext(StoresContext)
    const queryClient = useQueryClient()

    const buildQueryKey = () => [
        CLAIM_SEARCH_QUERY_KEY,
        rightsholderStore.currentRightsholderId,
        toJS(claimingToolSearchStore.claimSearchQuery),
        toJS(claimingToolSearchStore.claimFilter),
        claimingToolSearchStore.claimPaginationBody,
    ]

    const queryKey = buildQueryKey()
    const query = claimingToolSearchStore.claimSearchQuery
    const filter = toJS(claimingToolSearchStore.claimFilter)
    const pagination = claimingToolSearchStore.claimPaginationBody

    const claimQuery = useSearchQuery<Works>({
        queryKey,
        queryFn: async () => claimingToolSearchStore.fetchUnclaimedWorks(toJS(query) as WorkSearch, filter, pagination),
        // Copied into the store from the query's data, not from the fetch: a cache hit never runs the fetch.
        applyData: (response) => claimingToolSearchStore.applyUnclaimedWorks(response, pagination, queryKey),
        initialData: isEqual(claimingToolSearchStore.lastResultsKey, queryKey)
            ? claimingToolSearchStore.unclaimedWorks
            : undefined,
        // The tool never searches on load; only once criteria have been submitted.
        enabled: !!query && Object.keys(query).length > 0,
        onError: (error) => claimingToolSearchStore.reportSearchError(error),
    })

    const search = (searchCriteria: WorkSearch, filters: Partial<ClaimSearchFilter>) => {
        claimingToolSearchStore.setClaimSearchQuery(searchCriteria)
        claimingToolSearchStore.setClaimFilter(filters)
        claimingToolSearchStore.setClaimPaginationBody(DEFAULT_CLAIM_PAGINATION)
        // Resubmitting the same criteria, filter and first page leaves the key unchanged, so a key-driven
        // refetch alone wouldn't fire — the old button always searched again.
        if (isEqual(queryKey, buildQueryKey())) {
            queryClient.invalidateQueries({ queryKey: [CLAIM_SEARCH_QUERY_KEY] })
        }
    }

    const onChangePage = (body: PaginationBody) => claimingToolSearchStore.setClaimPaginationBody(body)

    const onChangeLimit = (newLimit: number) => claimingToolSearchStore.onChangeLimit(newLimit)

    return { claimQuery, search, onChangePage, onChangeLimit }
}

export default useClaimSearch
